/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ProductFilter from '../ProductFilter';
import BtnAddToGroup from '../BtnAddToGroup';
import BtnStatus from '../BtnStatus';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import { checkArray, createOptions, ProductInfoSortOption } from '../../../../Components/helper';
import SubmitAndDownloadPopup from '../SubmitPopup/SubmitAndDownload';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import { useProductInformationState } from '../Provider';
import { getOtherProductTags, updateProductGroup, updateProductStatus } from '../../store/DevelopmentOrder.action';
import BtnSortNew from '../btnSortNew';
import { NewSelectMultiSelect } from '../../../../Components/PIM/common/NewSelectMultiSelect';
import { NewSearch } from '../../../../Components/PIM/common/NewSearch';
import { AddIcon, ArchiveIcon, CloseIcon, DeleteIcon, BulkEditIcon, BulkSaveIcon, EditIcon, TableViewIcon, CardViewIcon, TagsIcon } from '../../../../bombiix-icon';
import { useProductEditNavigation } from '../../../../hooks/useProductEditNavigation';
import { Floaty, FloatyButton, FloatyGroup } from '../../../../Components/Floaty';
import './subheader.css';

/**
 * How long the tag picker waits after the last checkbox click before refetching. Ticking
 * several tags is one intent, not one request each — without this every click fired its own
 * call and the replies could land out of order, leaving the list showing a superseded result.
 */
const TAG_FILTER_DEBOUNCE_MS = 600;

/* CloseIcon is drawn as a small inline affordance — its X fills 45% of the 20-unit box, against
   65–80% for the action glyphs beside it on the bar — so at the same pixel size it reads
   undersized. Cropping the viewBox to the X plus the library's standard margin gives it EditIcon's
   proportion here, without changing CloseIcon for the chip and modal-close callers that depend on
   the small drawing. Same technique BulkSaveIcon uses to come the other way. `size` is passed
   through from FloatyButton, so this wrapper must not set one of its own. */
function BarCancelIcon(props) {
  return <CloseIcon {...props} viewBox="4.02 4.02 11.97 11.97" />;
}

function ProductInfoSubHeader({
  linkOpen,
  tableView = false,
  onAddProduct = null,
  inlineEditMode = 'add',
  inlineEditCount = 0,
  inlineEditBusy = false,
  onBulkEdit = null,
  onBulkSave = null,
  onBulkCancel = null,
  /*
   * Products covered by the selection. A ticked cluster parent brings its reorder children, which
   * have no checkbox of their own, so the ticked-id count understates what is actually selected.
   * The table view passes the expanded figure; the card list omits it and keeps the ticked count.
   */
  selectedProductCount = null,
  onAppendDraft = null,
  canAppendDraft = true,
}) {
  const reducer = useSelector((reducerState) => reducerState);
  const dispatch = useDispatch();
  const { ProductListData, UserDetails, SystemMastersDetails, Users } = reducer;
  const { isSupplierLoggedIn } = UserDetails;
  const { productGroups } = ProductListData;
  const { productStatusList } = SystemMastersDetails;
  const { logInRights, sectionAccess } = Users;
  const { CheckActionPermission } = useComponent();
  const ProductGroupToggle = sectionAccess?.masters?.productGroupsAccess;
  const ProductGroupAddEditAccess = CheckActionPermission(logInRights, 'masters', 'productGroups', 'addEdit');
  const ProductAddEditAccess = CheckActionPermission(logInRights, 'products', 'addProduct', 'addEdit');
  const ProductDeleteAccess = CheckActionPermission(logInRights, 'products', 'addProduct', 'delete');
  const { state, setState, callGetDevorderListApi, userRole } = useProductInformationState();
  const { t } = useTranslation('common');
  const history = useHistory();
  const { goToProductCreate } = useProductEditNavigation();

  const {
    searchText,
    filterData,
    filterSort,
    activeList,
    selectedIds,
    isArchivedModal,
    isTrashModal,
    selectProductTag,
    tagsOption,
    activeTags,
    deleteTechPackState,
    archieveTechPackState,
    trashTechPackState,
    openTechPackState,
    selectedGroup,
  } = state;

  const tagFetchTimerRef = useRef(null);

  /**
   * Drops a queued tag refetch. Called before scheduling the next one and whenever the tag
   * picker goes away (mode toggled off, Bin opened, component unmounted) so a superseded
   * request can never land on a list the user has already moved on from.
   * @returns {void}
   */
  const cancelPendingTagFetch = () => {
    if (!tagFetchTimerRef.current) return;
    clearTimeout(tagFetchTimerRef.current);
    tagFetchTimerRef.current = null;
  };

  useEffect(() => cancelPendingTagFetch, []);

  const openModal = () => {
    goToProductCreate();
  };

  const handleFilterProductGroup = async (val) => {
    setState((prevData) => ({
      ...prevData,
      productData: [],
      selectedGroupId: val?._id,
      pageIndex: 0,
      pageLoading: true,
      selectedGroup: val,
    }));
    await callGetDevorderListApi({
      archived: false,
      count: 0,
      groupId: val?._id,
      searchText,
      ...filterData,
      ...filterSort,
    });
  };

  /**
   * Opens the Bulk Edit page, carrying the currently selected product ids
   * through router location state so the module can act on them.
   * @returns {void}
   */
  const handleBulkEdit = () => {
    history.push({ pathname: '/modules-bulk-edit', state: { selectedIds } });
  };

  const handleApplyFilter = async (filterObj) => {
    setState((prevData) => ({
      ...prevData,
      filterData: filterObj,
      // productData: [],
      pageIndex: 0,
      pageLoading: true,
    }));
    const object = {
      archived: activeList !== 'open',
      deleted: activeList === 'bin',
      searchText,
      count: 0,
      ...filterObj,
      ...filterSort,
    };
    if (state?.selectedGroupId) {
      object.groupId = state?.selectedGroupId;
    }
    await callGetDevorderListApi(object);
  };

  const fetchProductsBySearchText = async (nextSearchText) => {
    setState((prevData) => ({
      ...prevData,
      searchText: nextSearchText,
      pageIndex: 0,
      pageLoading: true,
    }));
    await callGetDevorderListApi({
      archived: activeList !== 'open',
      count: 0,
      searchText: nextSearchText,
      ...filterData,
      ...filterSort,
    });
  };

  const handleProductSearchTextChange = (nextSearchText) => {
    if (nextSearchText === '') {
      fetchProductsBySearchText('');
      return;
    }
    setState((prevData) => ({
      ...prevData,
      searchText: nextSearchText,
    }));
  };

  const handleSearchProduct = () => fetchProductsBySearchText(searchText);

  const handleSearchBin = async (nextSearchText) => {
    setState((prevData) => ({
      ...prevData,
      searchText: nextSearchText,
      pageLoading: true,
    }));
    await callGetDevorderListApi({
      archived: true,
      deleted: true,
      searchText: nextSearchText,
      ...filterData,
    });
  };

  /**
   * Bin has no tag toggle, so the tag term is reset on the way in — otherwise the bin list
   * would stay filtered by tags the user can no longer see or clear.
   * @returns {Promise<void>}
   */
  const fetchBinProduct = async () => {
    cancelPendingTagFetch();
    setState((prevData) => ({
      ...prevData,
      productData: [],
      productTotalCount: 0,
      activeList: 'bin',
      pageIndex: 0,
      pageLoading: true,
      selectProductTag: 'product',
      tagsOption: [],
      activeTags: [],
    }));
    await callGetDevorderListApi({
      archived: true,
      deleted: true,
      count: 0,
      searchText,
      searchedTags: [],
      ...filterData,
    });
  };

  const fetchOpenProduct = async () => {
    setState((prevData) => ({
      ...prevData,
      productData: [],
      productTotalCount: 0,
      activeList: 'open',
      pageIndex: 0,
      pageLoading: true,
      selectedGroup: null,
      selectedGroupId: null,
    }));
    await callGetDevorderListApi({
      archived: false,
      count: 0,
      searchText,
      ...filterData,
    });
  };

  const fetchArchivedProduct = async () => {
    setState((prevData) => ({
      ...prevData,
      productData: [],
      productTotalCount: 0,
      activeList: 'archived',
      pageIndex: 0,
      pageLoading: true,
    }));
    await callGetDevorderListApi({
      archived: true,
      count: 0,
      searchText,
      ...filterData,
    });
  };

  const handleArchive = () => {
    if (checkArray(selectedIds)) {
      setState((prevData) => ({
        ...prevData,
        isArchivedModal: !isArchivedModal,
      }));
    } else {
      toasterMsg(t('DEVLOPMENT_ORDER.SUB_HEADER.SUB_HEADER_INDEX.PRODUCT_ARCHIVE'), 'danger');
    }
  };

  const handleTrash = () => {
    if (checkArray(selectedIds)) {
      setState((prevData) => ({
        ...prevData,
        isTrashModal: !isTrashModal,
      }));
    } else {
      toasterMsg(t('DEVLOPMENT_ORDER.SUB_HEADER.SUB_HEADER_INDEX.PRODUCT_ARCHIVE'), 'danger');
    }
  };

  const handleAddToGroup = async (val) => {
    if (checkArray(selectedIds)) {
      const objAddGrp = {
        prodId: selectedIds,
        productGroupIds: [val?._id],
        isAddNew: true,
      };
      const res = await dispatch(updateProductGroup(objAddGrp, t));
      if (res === 200) {
        setState((prevData) => ({
          ...prevData,
          selectedIds: [],
        }));
      }
    } else {
      toasterMsg(t('DEVLOPMENT_ORDER.SUB_HEADER.SUB_HEADER_INDEX.PLEASE_SELECT_PRODUCT'), 'danger');
    }
  };

  const handleProductStatus = async (val) => {
    if (checkArray(selectedIds)) {
      const objStatus = {
        statusId: val?._id,
        selectedProducts: selectedIds,
      };
      const res = await dispatch(updateProductStatus(objStatus, t));
      if (res === 200) {
        setState((prevData) => ({
          ...prevData,
          pageIndex: 0,
          pageLoading: true,
        }));
        await callGetDevorderListApi({
          archived: activeList !== 'open',
          count: 0,
          searchText,
          ...filterData,
          ...filterSort,
        });
        setState((prevData) => ({
          ...prevData,
          selectedIds: [],
        }));
      }
    } else {
      toasterMsg(t('DEVLOPMENT_ORDER.SUB_HEADER.SUB_HEADER_INDEX.PLEASE_SELECT_PRODUCT'), 'danger');
    }
  };

  const handleSortBy = async (val) => {
    setState((prevData) => ({
      ...prevData,
      filterSort: val,
      //   productData: [],
      //   pageIndex: 0,
      pageLoading: true,
    }));
    await callGetDevorderListApi({
      archived: false,
      count: 0,
      ...val,
      searchText,
      ...filterData,
    });
  };

  /**
   * Refetches the list for the active search mode. Product mode and tag mode are mutually
   * exclusive, so whichever term is not passed is dropped from the payload — that is what
   * clears the other mode's filter server-side.
   * @param {{ nextSearchText?: string, nextTags?: string[] }} overrides
   * @returns {Promise<void>}
   */
  const fetchProductsForSearchMode = async ({ nextSearchText = '', nextTags = [] }) => {
    setState((prevData) => ({
      ...prevData,
      productData: [],
      pageIndex: 0,
      pageLoading: true,
      searchText: nextSearchText,
      activeTags: nextTags,
    }));
    const payloadObj = {
      archived: activeList !== 'open',
      deleted: activeList === 'bin',
      count: 0,
      searchText: nextSearchText,
      searchedTags: nextTags,
      ...filterData,
      ...filterSort,
    };
    if (state?.selectedGroupId) {
      payloadObj.groupId = state?.selectedGroupId;
    }
    await callGetDevorderListApi(payloadObj);
  };

  /**
   * Toggles the subheader control between its default free-text product search and the tag
   * picker. Tag options are loaded lazily the first time the toggle is switched on. Leaving a
   * mode drops that mode's term so the list never stays filtered by a control the user can no
   * longer see.
   * @returns {Promise<void>}
   */
  const handleToggleTagMode = async () => {
    cancelPendingTagFetch();
    if (selectProductTag === 'tags') {
      setState((prevData) => ({
        ...prevData,
        selectProductTag: 'product',
        tagsOption: [],
      }));
      if (checkArray(activeTags)) await fetchProductsForSearchMode({});
      return;
    }
    const tags = await dispatch(getOtherProductTags());
    setState((prevData) => ({
      ...prevData,
      selectProductTag: 'tags',
      tagsOption: createOptions(tags, 'label', 'value', true),
    }));
    if (searchText) await fetchProductsForSearchMode({});
  };

  /**
   * Ticking a checkbox updates `activeTags` at once so the picker responds instantly, but the
   * refetch is deferred until the user stops clicking. Each click restarts the timer, so a
   * burst of ticks costs one request instead of one per tick.
   * @param {string[]} tags
   * @returns {void}
   */
  const handleTagChange = (tags) => {
    setState((prevData) => ({ ...prevData, activeTags: tags }));
    cancelPendingTagFetch();
    tagFetchTimerRef.current = setTimeout(() => {
      tagFetchTimerRef.current = null;
      fetchProductsForSearchMode({ nextTags: tags });
    }, TAG_FILTER_DEBOUNCE_MS);
  };

  const handleOpenProductGroup = () => {
    setState((prevData) => ({
      ...prevData,
      isAddProductGroup: true,
    }));
  };

  const productStatusOptions = useMemo(() => {
    if (checkArray(productStatusList)) {
      return productStatusList;
    }
    return [];
  }, [productStatusList]);

  const productGroupOption = useMemo(() => {
    if (checkArray(productGroups)) {
      return productGroups.filter((data) => data.isActive);
    }
    return [];
  }, [productGroups]);

  const nonBinSearch = (
    <div className="product-search-mode">
      <button
        type="button"
        aria-label={t('FILTER.SELECT_TAGS')}
        aria-pressed={selectProductTag === 'tags'}
        className={selectProductTag === 'tags' ? 'bombiix-icon-container bombiix-icon-container--green' : 'bombiix-icon-container'}
        onClick={() => handleToggleTagMode()}
      >
        <TagsIcon size={28} />
      </button>
      {selectProductTag === 'product' ? (
        <NewSearch
          className="bombiix-search"
          width={200}
          value={searchText}
          onChange={handleProductSearchTextChange}
          onEnter={handleSearchProduct}
          placeholder={t('FILTER.SEARCH_PRODUCT')}
        />
      ) : (
        <NewSelectMultiSelect
          isMulti
          isClearable
          showSearch
          name="tags"
          containerClassName="product-search-mode__tags"
          options={tagsOption}
          value={tagsOption?.filter((val) => checkArray(tagsOption) && activeTags?.includes(val?.value))}
          placeholder={t('FILTER.SELECT_TAGS')}
          selectAllLabel={t('COMMON_LABEL.SELECT_ALL')}
          onChange={(selected) => handleTagChange((selected || []).map((val) => val.value))}
        />
      )}
    </div>
  );

  return (
    <>
      {!linkOpen && (
        <>
          <div className="title-container d-flex align-items-center">
            <h4 className="title">
              {t('FILTER.PRODUCTS')}
              {' | '}
              <span className="page-type">{activeList}</span>
            </h4>
            {activeList === 'open' && ProductGroupToggle && (
              <div style={{ minWidth: '220px' }} className="ml-16">
                <NewSelectMultiSelect
                  options={productGroupOption}
                  isClearable
                  onChange={handleFilterProductGroup}
                  getOptionLabel={(option) => `${option.name}`}
                  getOptionValue={(option) => `${option._id}`}
                  placeholder={t('FILTER.SELECT_PRODUCT_GROUP')}
                  value={selectedGroup}
                />
              </div>
            )}
          </div>
          <div className="search-filter product-info-subheader-bw">
            {selectedIds?.length > 0 && (
              <span className="d-none d-md-inline-block product-selected-count text-medium text-black mr-16">
                {selectedProductCount ?? selectedIds?.length}
                {t('FILTER.PRODUCT_SELECTED')}
              </span>
            )}
            {activeList !== 'bin' ? (
              nonBinSearch
            ) : (
              <NewSearch className="bombiix-search" width={250} value={searchText} onChange={handleSearchBin} placeholder={t('FILTER.SEARCH_PRODUCTS')} />
            )}
            <div className="action-icons-container">
              <ProductFilter applyFilter={handleApplyFilter} filterData={filterData} activeList={activeList} />
              {activeList === 'open' && <BtnSortNew options={ProductInfoSortOption} onBtnClick={handleSortBy} val={filterSort} />}
              {activeList !== 'open' && (
                <button type="button" className="btn bombiix-btn btn-titlebar" onClick={() => fetchOpenProduct()}>
                  {t('FILTER.OPEN_PRODUCTS')}
                </button>
              )}
              {activeList !== 'archived' && ((openTechPackState && userRole !== 'Admin') || userRole === 'Admin') && (
                <button type="button" className="btn bombiix-btn btn-titlebar" onClick={() => fetchArchivedProduct()}>
                  {t('FILTER.ARCHIVED_PRODUCT')}
                </button>
              )}
              {!isSupplierLoggedIn && activeList === 'archived' && ProductDeleteAccess && (
                <button className="btn bombiix-btn btn-titlebar" type="button" title={t('FILTER.BIN')} onClick={() => fetchBinProduct()}>
                  {t('FILTER.BIN')}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      <Floaty position="fixed">
        {!['bin', 'archived'].includes(activeList) && ProductAddEditAccess && !linkOpen && !isSupplierLoggedIn && activeList === 'open' && (
          <>
            {/* One slot, three states (BM-9596): Add with no selection, Edit Selected with a
                valid selection, Save All once rows are open. Card view never leaves 'add'. */}
            {inlineEditMode === 'edit' && tableView && (
              /* EditIcon, not BulkEditIcon: the "Bulk Edit Modules" action below owns BulkEditIcon,
                 and two different actions sharing one glyph on the same bar is unreadable. */
              <FloatyButton icon={EditIcon} label={t('FILTER.EDIT_SELECTED')} onClick={() => onBulkEdit && onBulkEdit()} />
            )}
            {inlineEditMode === 'save' && tableView && (
              <FloatyGroup>
                {/* No variant: `green` is the one permanently-filled variant, so it sat coloured on
                    the bar while every neighbour stayed transparent until hovered. The default
                    matches the rest of the bar and the Save control on the BOM page. */}
                <FloatyButton
                  icon={BulkSaveIcon}
                  label={inlineEditCount > 0 ? `${t('COMMON_LABEL.SAVE')} (${inlineEditCount})` : t('COMMON_LABEL.SAVE')}
                  disabled={inlineEditBusy}
                  onClick={() => onBulkSave && onBulkSave()}
                />
                <FloatyButton
                  icon={BarCancelIcon}
                  variant="red"
                  label={t('COMMON_LABEL.CANCEL')}
                  disabled={inlineEditBusy}
                  onClick={() => onBulkCancel && onBulkCancel()}
                />
              </FloatyGroup>
            )}
            {inlineEditMode === 'draft' && tableView && (
              <FloatyGroup>
                <FloatyButton
                  icon={AddIcon}
                  label={t('FILTER.ADD_PRODUCT')}
                  disabled={inlineEditBusy || !canAppendDraft}
                  onClick={() => onAppendDraft && onAppendDraft()}
                />
                <FloatyButton
                  icon={BulkSaveIcon}
                  label={`${t('COMMON_LABEL.SAVE')} (${inlineEditCount})`}
                  disabled={inlineEditBusy}
                  onClick={() => onBulkSave && onBulkSave()}
                />
                <FloatyButton
                  icon={BarCancelIcon}
                  variant="red"
                  label={t('COMMON_LABEL.CANCEL')}
                  disabled={inlineEditBusy}
                  onClick={() => onBulkCancel && onBulkCancel()}
                />
              </FloatyGroup>
            )}
            {(inlineEditMode === 'add' || !tableView) && (
              <FloatyButton icon={AddIcon} label={t('FILTER.ADD_PRODUCT')} onClick={() => (tableView ? onAddProduct && onAddProduct() : openModal())} />
            )}
          </>
        )}
        {ProductAddEditAccess && ProductGroupAddEditAccess && ProductGroupToggle && !isSupplierLoggedIn && activeList === 'open' && (
          <BtnAddToGroup selectedIds={selectedIds} options={productGroupOption} onBtnClick={handleAddToGroup} handleOpenProductGroup={handleOpenProductGroup} />
        )}
        {((userRole !== 'Admin' && (['open', 'reorder'].includes(state?.activeList) ? archieveTechPackState : deleteTechPackState)) || userRole === 'Admin') &&
          !isSupplierLoggedIn &&
          activeList !== 'bin' &&
          ProductAddEditAccess && (
            <FloatyButton
              icon={ArchiveIcon}
              label={activeList === 'open' ? t('FILTER.ARCHIVE') : t('FILTER.UNARCHIVE')}
              variant="dark"
              disabled={selectedIds?.length === 0}
              onClick={() => handleArchive()}
            />
          )}
        {ProductAddEditAccess && !isSupplierLoggedIn && activeList === 'open' && (
          <BtnStatus selectedIds={selectedIds} options={productStatusOptions} onBtnClick={handleProductStatus} />
        )}
        {!['bin', 'archived'].includes(activeList) && !isSupplierLoggedIn && (userRole === 'Admin' ? true : logInRights?.[0]?.submitTechPack) && (
          <SubmitAndDownloadPopup mainStateData={state} setMainState={setState} />
        )}

        {/* Hidden rather than disabled with no selection: it acts on the ticked products, so with
            none ticked there is nothing for it to do and the slot is better given back. */}
        {ProductAddEditAccess && !isSupplierLoggedIn && activeList === 'open' && selectedIds?.length > 0 && (
          <FloatyButton icon={BulkEditIcon} label={t('bulk-edit:TOOLTIP')} onClick={() => handleBulkEdit()} />
        )}
        {!isSupplierLoggedIn && activeList === 'archived' && ProductDeleteAccess && ((trashTechPackState && userRole !== 'Admin') || userRole === 'Admin') && (
          <FloatyButton icon={DeleteIcon} label={t('FILTER.TRASH')} variant="red" disabled={selectedIds?.length === 0} onClick={() => handleTrash()} />
        )}
        {!linkOpen &&
          (tableView ? (
            <FloatyButton
              icon={CardViewIcon}
              label={t('FILTER.CARD_VIEW')}
              className="view-toggle-btn active"
              onClick={() => {
                sessionStorage.setItem('productInfoView', 'card');
                history.push('/development-order-overview');
              }}
            />
          ) : (
            <FloatyButton
              icon={TableViewIcon}
              label={t('FILTER.TABLE_VIEW')}
              variant="khaki"
              className="view-toggle-btn"
              onClick={() => {
                sessionStorage.setItem('productInfoView', 'table');
                history.push('/development-order-overview/table-view');
              }}
            />
          ))}
      </Floaty>
    </>
  );
}

export default ProductInfoSubHeader;
